import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DetailsViewComponent,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { valueOrDefault } from '@web/utils';
import {
  AdminInvoiceSettingsFormData,
  AdminInvoiceSettingsFormGroup,
} from '../../models/admin-invoice-settings-form-data';
import { ToggleSwitch } from 'primeng/toggleswitch';

@Component({
  selector: 'app-admin-invoice-settings-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DetailsViewComponent,
    FormControlComponent,
    Button,
    PrimengButtonDirective,
    InputText,
    PrimengInputTextDirective,
    ToggleSwitch,
  ],
  templateUrl: './admin-invoice-settings-form.component.html',
  styleUrl: './admin-invoice-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminInvoiceSettingsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);

  public readonly data = input.required<AdminInvoiceSettingsFormData>();
  public readonly submitClickEvent = output<AdminInvoiceSettingsFormData>();

  protected readonly form = this.buildForm();

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  protected get isConnectedWithKsef(): boolean {
    return this.data().isConnectedWithKsef;
  }

  private toData(): AdminInvoiceSettingsFormData {
    const data = this.form.getRawValue();

    return new AdminInvoiceSettingsFormData({
      isConnectedWithKsef: this.data().isConnectedWithKsef,
      sendToKsef: valueOrDefault(data.sendToKsef, false),
      seller: valueOrDefault(data.seller, ''),
      temporaryInvoiceBankName: valueOrDefault(
        data.temporaryInvoiceBankName,
        '',
      ),
      temporaryInvoiceAccount: valueOrDefault(data.temporaryInvoiceAccount, ''),
    });
  }

  private buildForm(): AdminInvoiceSettingsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      sendToKsef: this.builder.control<boolean | null>(null),
      seller: this.builder.control<string | null>(null, [Validators.required, Validators.maxLength(AdminInvoiceSettingsFormData.SellerMaxLength)]),
      temporaryInvoiceBankName: this.builder.control<string | null>(null, [Validators.required, Validators.maxLength(AdminInvoiceSettingsFormData.TemporaryInvoiceBankNameMaxLength)]),
      temporaryInvoiceAccount: this.builder.control<string | null>(null, [Validators.required, Validators.maxLength(AdminInvoiceSettingsFormData.TemporaryInvoiceAccountMaxLength)]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue(
      {
        sendToKsef: data.sendToKsef,
        seller: data.seller,
        temporaryInvoiceBankName: data.temporaryInvoiceBankName,
        temporaryInvoiceAccount: data.temporaryInvoiceAccount,
      },
      { emitEvent: false },
    );
  }
}
